import {
  Box, Grid,
} from '@mui/material';
import { useEffect, useState } from 'react';
import EditableText from '../../Form/EditableText';
import CloseButton from '../../Buttons/Close';
import Utils from '../../../utils/Utils';
import BlockProps from '../../../models/BlockProps';
import BlockTitleTextButtonData from './BlockTitleTextButtonData';
import { useGetBase64Image } from '../../../hooks/UseGetBase64Image';
import BlockData from '../../../models/BlockData';
import LinkButton from '../../Buttons/LinkButton';
import BlockComponentButton from '../../../models/BlockComponentValues/BlockComponentButton';
import BlockSettings, { BlockAlign, BlockHeight } from '../../../models/BlockSettings';
import GridWithSettings from '../../Layout/GridWithSettings';
import styles from './BlockTitleTextButton.module.css';

export interface BlockTitleTextButtonProps extends BlockProps {
  blockData: BlockData[]
}

function BlockTitleTextButton({
  data, isEditing, isTemplate, editHandler, blockData, previewMode,
}: BlockTitleTextButtonProps) {
  const props = (data.data as BlockTitleTextButtonData);
  const [minHeight, setMinHeight] = useState('');
  const [maxHeight, setMaxHeight] = useState('');
  const [gridSettings, setGridSettings] = useState<BlockSettings>(props.settings);

  const handleUpdateSettings = (settings?: BlockSettings) => {
    props.settings = settings;
    setGridSettings(settings);
    editHandler(data.id, props);
  };

  useEffect(() => {
    if (props.height === BlockHeight.TALL) {
      setMinHeight('600px');
      setMaxHeight(null);
    } else {
      setMinHeight(null);
      setMaxHeight(null);
    }

    setGridSettings(props.settings);
  }, [props.height]);

  const handleTitleUpdate = (text?: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const handleDescriptionUpdate = (text?: string) => {
    props.description.value = text;
    editHandler(data.id, props);
  };

  const handleUpdateButton = (button?: BlockComponentButton) => {
    props.button = button;
    editHandler(data.id, props);
  };

  let titleStyle = styles.title;
  if (previewMode === 'smartphone') {
    titleStyle = styles.blockStretched;
  } else if (props.settings.align === BlockAlign.STRETCH) {
    titleStyle = styles.blockStretched;
  } else if (props.settings.align === BlockAlign.CENTER) {
    titleStyle = styles.titleStretched;
  } else {
    titleStyle = styles.title;
  }

  let blockStyle = styles.block;
  if (previewMode === 'smartphone') {
    blockStyle = styles.blockStretched;
  } else if (props.settings.align === BlockAlign.STRETCH) {
    blockStyle = styles.blockStretched;
  } else {
    blockStyle = styles.block;
  }

  const base64Image = useGetBase64Image(props.url?.value);

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: `${minHeight}`,
        maxHeight: `${maxHeight}`,
        overflow: 'hidden',
        backgroundImage: base64Image ? `url(${base64Image})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <GridWithSettings
        settings={gridSettings}
        updateHandler={handleUpdateSettings}
        isEditing={isEditing}
        hideJustify
      >

        {props.title && (
          <Grid className={titleStyle}>
            <Box
              sx={
              [Utils.hoverBorder(isEditing),
                {
                  display: 'inline-block',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  height: '100%',
                  width: '100%',
                }]
              }
            >

              {isEditing && (
                <CloseButton updateHandler={() => {
                  props.title = undefined;
                  editHandler(data.id, props);
                }}
                />
              )}

              <EditableText
                text={props.title.value}
                isEditable={isEditing && !isTemplate}
                updateHandler={handleTitleUpdate}
              />
            </Box>
          </Grid>
        )}

        {props.description && (
          <Grid
            item
            xs={12}
            className={blockStyle}
            sx={Utils.hoverBorder(isEditing, {
              marginTop: 2,
            })}
          >

            {isEditing && (
              <CloseButton updateHandler={() => {
                props.description = undefined;
                editHandler(data.id, props);
              }}
              />
            )}

            <EditableText
              text={props.description.value}
              isEditable={isEditing && !isTemplate}
              updateHandler={handleDescriptionUpdate}
            />

          </Grid>
        )}

        {props.button && (
          <Grid
            item
            className={blockStyle}
            xs={12}
            sx={Utils.hoverBorder(isEditing, {
              marginTop: 2,
            })}
          >

            {isEditing && (
              <CloseButton updateHandler={() => {
                props.button = undefined;
                editHandler(data.id, props);
              }}
              />
            )}

            <LinkButton
              button={props.button}
              isEditable={isEditing && !isTemplate}
              isTemplate={isTemplate}
              updateHandler={handleUpdateButton}
              blockData={blockData}
            />

          </Grid>
        )}
      </GridWithSettings>

    </Grid>
  );
}

export default BlockTitleTextButton;
